import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";
import { ProxyContextType, ProxySession, ProxyUser, CreateProxySessionRequest } from '@/types/proxy';
import { ProxyAPI } from '@/utils/proxyApi';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ProxyContext = createContext<ProxyContextType | undefined>(undefined);

interface ProxyProviderProps {
  children: React.ReactNode;
}

const PROXY_STORAGE_KEY = 'proxy_session_data';
const PROXY_ACTIVITY_KEY = 'proxy_last_activity';

export function ProxyProvider({ children }: ProxyProviderProps) {
  // State management
  const [isProxyMode, setIsProxyMode] = useState(false);
  const [proxySession, setProxySession] = useState<ProxySession | null>(null);
  const [targetUser, setTargetUser] = useState<ProxyUser | null>(null);
  const [targetPermissions, setTargetPermissions] = useState<string[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  
  // Error handling
  const [error, setError] = useState<string | null>(null);
  
  // Refs for intervals and cleanup
  const sessionValidationInterval = useRef<NodeJS.Timeout>();
  const timeUpdateInterval = useRef<NodeJS.Timeout>();
  const activityTrackerInterval = useRef<NodeJS.Timeout>();
  const mountedRef = useRef(true);
  
  const { user } = useAuth();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Update activity timestamp
   */
  const updateActivity = useCallback(() => {
    if (isProxyMode) {
      localStorage.setItem(PROXY_ACTIVITY_KEY, Date.now().toString());
    }
  }, [isProxyMode]);

  /**
   * Handle user activity events
   */
  const handleActivity = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  /**
   * Setup activity tracking
   */
  useEffect(() => {
    if (!isProxyMode) return;

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Track activity every 30 seconds
    activityTrackerInterval.current = setInterval(updateActivity, 30000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (activityTrackerInterval.current) {
        clearInterval(activityTrackerInterval.current);
      }
    };
  }, [isProxyMode, handleActivity, updateActivity]);

  /**
   * Persist proxy session to localStorage for multi-tab sync
   */
  const persistProxySession = useCallback((session: ProxySession | null, user: ProxyUser | null) => {
    if (session && user) {
      const data = {
        session,
        targetUser: user,
        timestamp: Date.now()
      };
      localStorage.setItem(PROXY_STORAGE_KEY, JSON.stringify(data));
      updateActivity();
    } else {
      localStorage.removeItem(PROXY_STORAGE_KEY);
      localStorage.removeItem(PROXY_ACTIVITY_KEY);
    }

    // Broadcast to other tabs
    window.dispatchEvent(new CustomEvent('proxy-session-change', {
      detail: { session, targetUser: user }
    }));
  }, [updateActivity]);

  /**
   * Load proxy session from localStorage
   */
  const loadPersistedSession = useCallback(async () => {
    try {
      const stored = localStorage.getItem(PROXY_STORAGE_KEY);
      if (!stored) return;

      const data = JSON.parse(stored);
      const { session, targetUser: storedUser } = data;

      // Validate session is still active
      const validSession = await ProxyAPI.validateProxySession(session.session_token);
      
      if (!mountedRef.current) return;
      
      if (validSession) {
        setProxySession(validSession);
        setTargetUser(storedUser);
        setIsProxyMode(true);

        // Load target permissions
        const permissions = await ProxyAPI.getUserPermissions(storedUser.id);
        
        if (!mountedRef.current) return;
        
        setTargetPermissions(permissions);
      } else {
        // Session expired, clean up
        localStorage.removeItem(PROXY_STORAGE_KEY);
        localStorage.removeItem(PROXY_ACTIVITY_KEY);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      
      Sentry.captureException(error, {
        tags: { context: 'proxy_session', component: 'ProxyContext' },
        extra: { message: 'Error loading persisted proxy session' }
      });
      localStorage.removeItem(PROXY_STORAGE_KEY);
    }
  }, []);

  /**
   * Calculate time remaining in session
   */
  const calculateTimeRemaining = useCallback(() => {
    if (!proxySession) return 0;
    
    const now = Date.now();
    const expiresAt = new Date(proxySession.expires_at).getTime();
    const remaining = Math.max(0, expiresAt - now);
    
    return Math.floor(remaining / 1000); // Return seconds
  }, [proxySession]);

  /**
   * Update time remaining counter
   */
  useEffect(() => {
    if (!isProxyMode || !proxySession) return;

    const updateTimer = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Auto-logout if session expired
      if (remaining <= 0) {
        toast.error('Proxy session expired');
        endProxySession();
      } else if (remaining <= 300) { // 5 minutes warning
        if (remaining % 60 === 0) { // Only show every minute
          toast.warning(`Proxy session expires in ${Math.floor(remaining / 60)} minutes`);
        }
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    timeUpdateInterval.current = setInterval(updateTimer, 1000);

    return () => {
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }
    };
  }, [isProxyMode, proxySession, calculateTimeRemaining]);

  /**
   * Validate session periodically
   */
  useEffect(() => {
    if (!isProxyMode || !proxySession) return;

    const validateSession = async () => {
      if (!proxySession) return;
      
      const valid = await ProxyAPI.validateProxySession(proxySession.session_token);
      if (!valid) {
        toast.error('Proxy session is no longer valid');
        await endProxySession();
      }
    };

    // Validate every 2 minutes
    sessionValidationInterval.current = setInterval(validateSession, 2 * 60 * 1000);

    return () => {
      if (sessionValidationInterval.current) {
        clearInterval(sessionValidationInterval.current);
      }
    };
  }, [isProxyMode, proxySession]);

  /**
   * Listen for cross-tab session changes
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PROXY_STORAGE_KEY) {
        if (e.newValue) {
          const data = JSON.parse(e.newValue);
          setProxySession(data.session);
          setTargetUser(data.targetUser);
          setIsProxyMode(true);
        } else {
          setProxySession(null);
          setTargetUser(null);
          setIsProxyMode(false);
          setTargetPermissions([]);
        }
      }
    };

    const handleProxyChange = (e: CustomEvent) => {
      const { session, targetUser } = e.detail;
      if (session && targetUser) {
        setProxySession(session);
        setTargetUser(targetUser);
        setIsProxyMode(true);
      } else {
        setProxySession(null);
        setTargetUser(null);
        setIsProxyMode(false);
        setTargetPermissions([]);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('proxy-session-change', handleProxyChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('proxy-session-change', handleProxyChange as EventListener);
    };
  }, []);

  /**
   * Initialize proxy context
   */
  useEffect(() => {
    if (user) {
      loadPersistedSession();
    }
  }, [user, loadPersistedSession]);

  /**
   * Start a new proxy session
   */
  const startProxySession = useCallback(async (request: CreateProxySessionRequest) => {
    if (isStarting) return;
    
    setIsStarting(true);
    setError(null);

    try {
      const response = await ProxyAPI.createProxySession(request);
      
      if (!mountedRef.current) return;
      
      setProxySession(response.session);
      setTargetUser(response.target_user);
      setIsProxyMode(true);

      // Load target permissions
      const permissions = await ProxyAPI.getUserPermissions(response.target_user.id);
      
      if (!mountedRef.current) return;
      
      setTargetPermissions(permissions);

      // Persist for multi-tab sync
      persistProxySession(response.session, response.target_user);

      toast.success(response.message || 'Proxy session started successfully');
    } catch (error) {
      if (!mountedRef.current) return;
      
      const message = error instanceof Error ? error.message : 'Failed to start proxy session';
      setError(message);
      toast.error(message);
      throw error;
    } finally {
      if (mountedRef.current) {
        setIsStarting(false);
      }
    }
  }, [isStarting, persistProxySession]);

  /**
   * End the current proxy session
   */
  const endProxySession = useCallback(async () => {
    if (isEnding || !proxySession) return;
    
    setIsEnding(true);
    setError(null);

    try {
      await ProxyAPI.endProxySession(proxySession.id);
      
      if (!mountedRef.current) return;
      
      // Clear state
      setProxySession(null);
      setTargetUser(null);
      setIsProxyMode(false);
      setTargetPermissions([]);
      setTimeRemaining(0);

      // Clear persistence
      persistProxySession(null, null);

      toast.success('Proxy session ended successfully');
    } catch (error) {
      if (!mountedRef.current) return;
      
      const message = error instanceof Error ? error.message : 'Failed to end proxy session';
      setError(message);
      toast.error(message);
    } finally {
      if (mountedRef.current) {
        setIsEnding(false);
      }
    }
  }, [isEnding, proxySession, persistProxySession]);

  /**
   * Validate current session
   */
  const validateSession = useCallback(async (): Promise<boolean> => {
    if (!proxySession) return false;

    try {
      const valid = await ProxyAPI.validateProxySession(proxySession.session_token);
      
      if (!mountedRef.current) return false;
      
      if (!valid) {
        await endProxySession();
        return false;
      }
      return true;
    } catch (error) {
      if (!mountedRef.current) return false;
      
      Sentry.captureException(error, {
        tags: { context: 'proxy_validation', component: 'ProxyContext' },
        extra: { message: 'Error validating session' }
      });
      return false;
    }
  }, [proxySession, endProxySession]);

  /**
   * Log proxy action
   */
  const logProxyAction = useCallback(async (action: string, details?: any) => {
    if (!proxySession) return;

    try {
      await ProxyAPI.logProxyAction(proxySession.id, action, details);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'proxy_logging', component: 'ProxyContext' },
        extra: { message: 'Error logging proxy action', action, details }
      });
    }
  }, [proxySession]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context value
  const contextValue: ProxyContextType = {
    // State
    isProxyMode,
    proxySession,
    targetUser,
    targetPermissions,
    timeRemaining,
    
    // Actions
    startProxySession,
    endProxySession,
    validateSession,
    logProxyAction,
    
    // Loading states
    isLoading,
    isStarting,
    isEnding,
    
    // Error handling
    error,
    clearError
  };

  return (
    <ProxyContext.Provider value={contextValue}>
      {children}
    </ProxyContext.Provider>
  );
}

export function useProxy(): ProxyContextType {
  const context = useContext(ProxyContext);
  if (context === undefined) {
    throw new Error('useProxy must be used within a ProxyProvider');
  }
  return context;
}